import React, { useEffect, useState } from 'react';
import {
  Table, Tag, Typography, Card, Row, Col, Statistic,
  Space, Button, Tooltip, Spin, Alert, Badge, Progress, Tabs, DatePicker, message,
} from 'antd';
import {
  DollarOutlined, CrownOutlined, TeamOutlined, WarningOutlined,
  ReloadOutlined, CheckCircleOutlined, DownloadOutlined, FileTextOutlined, EyeOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { theme as antdTheme } from 'antd';
import axios from 'axios';

const { Title, Text } = Typography;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });


const AdminBilling = () => {
  const { token: tk } = antdTheme.useToken();
  const navigate      = useNavigate();
  const [orgs,     setOrgs]     = useState([]);
  const [stats,    setStats]    = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invTotal, setInvTotal] = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [dateRange, setDateRange] = useState(null); // [dayjs, dayjs] | null
  const [exporting, setExporting] = useState(false);

  const invoiceQuery = () => {
    const params = new URLSearchParams({ limit: '200' });
    if (dateRange?.[0]) params.set('from', dateRange[0].format('YYYY-MM-DD'));
    if (dateRange?.[1]) params.set('to',   dateRange[1].format('YYYY-MM-DD'));
    return params.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const [orgsRes, statsRes, invRes] = await Promise.all([
        axios.get('/api/admin/orgs',      { headers: authHeader() }),
        axios.get('/api/admin/stats',     { headers: authHeader() }),
        axios.get(`/api/admin/invoices?${invoiceQuery()}`, { headers: authHeader() }),
      ]);
      setOrgs(orgsRes.data);
      setStats(statsRes.data);
      setInvoices(invRes.data.data || []);
      setInvTotal(invRes.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch invoices when the date filter changes
  const reloadInvoices = async () => {
    try {
      const invRes = await axios.get(`/api/admin/invoices?${invoiceQuery()}`, { headers: authHeader() });
      setInvoices(invRes.data.data || []);
      setInvTotal(invRes.data.total || 0);
    } catch { /* silent */ }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`/api/admin/invoices/export.csv?${invoiceQuery()}`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a   = document.createElement('a');
      const range = dateRange ? `${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}` : 'all';
      a.href = url; a.download = `invoices_${range}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const downloadInvoicePDF = async (inv) => {
    try {
      const res = await axios.get(`/api/admin/invoices/${inv.id}/pdf`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a   = document.createElement('a');
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  const viewInvoicePDF = async (inv) => {
    try {
      const res = await axios.get(`/api/admin/invoices/${inv.id}/pdf`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (error)   return <Alert type="error" message={error} showIcon style={{ borderRadius: 10 }} />;

  const proOrgs     = orgs.filter(o => o.plan === 'pro');
  const freeOrgs    = orgs.filter(o => o.plan !== 'pro');
  const pastDueOrgs = orgs.filter(o => o.plan_status === 'past_due');

  // Per-org monthly price by their subscription currency (₹999 INR / $9.99 USD)
  const PRICE_INR = 999;
  const PRICE_USD = 9.99;
  const orgMonthly = (o) => (o.billing_currency === 'INR' ? PRICE_INR : PRICE_USD);
  const orgCurrency = (o) => (o.billing_currency === 'INR' ? 'INR' : 'USD');
  const fmtOrgPrice = (o) => o.billing_currency === 'INR'
    ? `₹${PRICE_INR.toLocaleString('en-IN')}`
    : `$${PRICE_USD.toFixed(2)}`;

  // MRR split by currency (can't sum ₹ and $)
  const mrrInr = proOrgs.filter(o => o.billing_currency === 'INR').length * PRICE_INR;
  const mrrUsd = proOrgs.filter(o => o.billing_currency !== 'INR').length * PRICE_USD;

  // Revenue timeline — group pro signups by month
  const revenueByMonth = {};
  proOrgs.forEach(o => {
    const mo = new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    revenueByMonth[mo] = (revenueByMonth[mo] || 0) + 1;
  });

  const fmtCurrency = (cents, currency = 'usd') => {
    const cc = (currency || 'usd').toUpperCase();
    return new Intl.NumberFormat(cc === 'INR' ? 'en-IN' : 'en-US', { style: 'currency', currency: cc }).format((cents || 0) / 100);
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  const invoiceCols = [
    {
      title: 'Invoice #',
      key: 'number',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13, fontFamily: 'monospace' }}>{r.invoice_number}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.description}</Text>
        </Space>
      ),
    },
    {
      title: 'Organisation',
      key: 'org',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text
            style={{ color: tk.colorPrimary, cursor: 'pointer', fontSize: 13 }}
            onClick={() => navigate(`/admin/orgs/${r.org_id}`)}
          >{r.org_name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.org_email}</Text>
        </Space>
      ),
    },
    {
      title: 'Period',
      key: 'period',
      width: 200,
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {fmtDate(r.period_start)} – {fmtDate(r.period_end)}
        </Text>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: d => <Text style={{ fontSize: 12 }}>{fmtDate(d)}</Text>,
    },
    {
      title: 'GST / Tax',
      key: 'tax',
      width: 150,
      render: (_, r) => {
        if (r.is_export) return <Tag color="blue">Export · zero-rated</Tag>;
        if (!r.tax_amount) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        const label = r.tax_type === 'cgst_sgst' ? `CGST+SGST ${r.tax_rate}%` : `IGST ${r.tax_rate}%`;
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>{fmtCurrency(r.tax_amount, r.currency)}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
          </Space>
        );
      },
    },
    {
      title: 'GSTIN',
      dataIndex: 'customer_gstin',
      key: 'gstin',
      width: 150,
      render: g => g ? <Text style={{ fontSize: 11, fontFamily: 'monospace' }}>{g}</Text> : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
    },
    {
      title: 'Total',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (_, r) => <Text strong>{fmtCurrency(r.amount, r.currency)}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: s => <Tag color={s === 'paid' ? 'success' : s === 'open' ? 'warning' : 'default'}>{s}</Tag>,
    },
    {
      title: '',
      key: 'pdf',
      width: 80,
      render: (_, r) => (
        <Space size={2}>
          <Tooltip title="View PDF">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => viewInvoicePDF(r)} />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button type="text" size="small" icon={<DownloadOutlined />} onClick={() => downloadInvoicePDF(r)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const columns = [
    {
      title: 'Organisation',
      key: 'name',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text
            strong style={{ fontSize: 13, color: tk.colorPrimary, cursor: 'pointer' }}
            onClick={() => navigate(`/admin/orgs/${r.id}`)}
          >
            {r.name}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.owner_email}</Text>
        </Space>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 100,
      render: (plan, r) => (
        <Space direction="vertical" size={2}>
          <Tag
            icon={plan === 'pro' ? <CrownOutlined /> : null}
            color={plan === 'pro' ? 'gold' : 'default'}
          >
            {plan?.toUpperCase()}
          </Tag>
          {r.plan_status === 'past_due' && (
            <Tag color="orange" style={{ fontSize: 10 }}>Past Due</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'MRR',
      key: 'mrr',
      width: 100,
      align: 'right',
      render: (_, r) => (
        <Text strong style={{ color: r.plan === 'pro' ? '#10b981' : tk.colorTextQuaternary }}>
          {r.plan === 'pro' ? fmtOrgPrice(r) : '—'}
        </Text>
      ),
    },
    {
      title: 'Stripe Customer',
      dataIndex: 'stripe_customer_id',
      key: 'stripe',
      width: 180,
      render: v => v ? (
        <Text copyable={{ text: v }} style={{ fontSize: 11, color: tk.colorTextSecondary }}>
          {v.slice(0, 18)}…
        </Text>
      ) : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, r) => {
        if (r.suspended)           return <Badge status="error"   text="Suspended" />;
        if (r.plan_status === 'past_due') return <Badge status="warning" text="Past Due" />;
        if (r.plan === 'pro')      return <Badge status="success" text="Active" />;
        return <Badge status="default" text="Free" />;
      },
    },
    {
      title: 'Renews',
      dataIndex: 'plan_expires_at',
      key: 'renews',
      width: 130,
      render: d => d
        ? <Text style={{ fontSize: 12 }}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
        : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
    {
      title: 'Joined',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: d => <Text type="secondary" style={{ fontSize: 12 }}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>,
    },
  ];

  return (
    <div style={{ padding: '0 2px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>Billing Overview</Title>
          <Text type="secondary">Revenue, subscriptions and payment status across all organisations.</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </div>

      {pastDueOrgs.length > 0 && (
        <Alert
          type="warning" showIcon icon={<WarningOutlined />}
          message={`${pastDueOrgs.length} organisation${pastDueOrgs.length > 1 ? 's have' : ' has'} past-due payments.`}
          style={{ marginBottom: 16, borderRadius: 10 }}
        />
      )}

      {/* Top KPI cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          {
            title:  'MRR — India (INR)',
            value:  `₹${mrrInr.toLocaleString('en-IN')}`,
            sub:    `${proOrgs.filter(o => o.billing_currency === 'INR').length} INR customer${proOrgs.filter(o => o.billing_currency === 'INR').length !== 1 ? 's' : ''}`,
            icon:   <DollarOutlined />,
            color:  '#10b981',
          },
          {
            title:  'MRR — International (USD)',
            value:  `$${mrrUsd.toFixed(2)}`,
            sub:    `${proOrgs.filter(o => o.billing_currency !== 'INR').length} USD customer${proOrgs.filter(o => o.billing_currency !== 'INR').length !== 1 ? 's' : ''}`,
            icon:   <DollarOutlined />,
            color:  '#0a40d9',
          },
          {
            title:  'Pro Customers',
            value:  proOrgs.length,
            sub:    `${freeOrgs.length} on free tier`,
            icon:   <CrownOutlined />,
            color:  '#f59e0b',
          },
          {
            title:  'Past Due',
            value:  pastDueOrgs.length,
            sub:    pastDueOrgs.length ? 'Require attention' : 'All payments current',
            icon:   <WarningOutlined />,
            color:  pastDueOrgs.length ? '#ef4444' : '#9ca3af',
          },
        ].map(s => (
          <Col xs={12} sm={6} key={s.title}>
            <Card size="small" style={{ borderRadius: 10, border: `1px solid ${tk.colorBorderSecondary}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{s.title}</Text>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1.2 }}>{s.value}</div>
                  <Text type="secondary" style={{ fontSize: 11, marginTop: 2, display: 'block' }}>{s.sub}</Text>
                </div>
                <div style={{
                  width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                  background: `${s.color}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {React.cloneElement(s.icon, { style: { fontSize: 16, color: s.color } })}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Conversion funnel */}
      <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 16 }}>Plan Distribution</Text>
        <Row gutter={[24, 12]}>
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
              <Space>
                <CrownOutlined style={{ color: '#f59e0b' }} />
                <Text strong>Pro</Text>
              </Space>
              <Text type="secondary">{proOrgs.length} / {orgs.length} orgs</Text>
            </div>
            <Progress
              percent={orgs.length ? Math.round(proOrgs.length / orgs.length * 100) : 0}
              strokeColor="#f59e0b"
              showInfo={true}
              format={p => <Text style={{ fontSize: 12 }}>{p}%</Text>}
            />
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
              <Space>
                <TeamOutlined style={{ color: '#6b7280' }} />
                <Text strong>Free</Text>
              </Space>
              <Text type="secondary">{freeOrgs.length} / {orgs.length} orgs</Text>
            </div>
            <Progress
              percent={orgs.length ? Math.round(freeOrgs.length / orgs.length * 100) : 0}
              strokeColor="#6b7280"
              showInfo={true}
              format={p => <Text style={{ fontSize: 12 }}>{p}%</Text>}
            />
          </Col>
        </Row>
      </Card>

      {/* Tabs: Subscriptions | Invoices */}
      <Card
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
        styles={{ body: { padding: 0 } }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ padding: '0 20px' }}
          items={[
            {
              key: 'subscriptions',
              label: (
                <Space>
                  <CrownOutlined />
                  Subscriptions
                  <Tag style={{ marginLeft: 2 }}>{orgs.length}</Tag>
                </Space>
              ),
              children: (
                <div style={{ margin: '0 -20px' }}>
                  <div style={{
                    padding: '10px 20px 12px', borderBottom: `1px solid ${tk.colorBorderSecondary}`,
                    display: 'flex', gap: 8,
                  }}>
                    <Tag color="gold" icon={<CrownOutlined />}>{proOrgs.length} Pro</Tag>
                    <Tag>{freeOrgs.length} Free</Tag>
                    {pastDueOrgs.length > 0 && <Tag color="orange">{pastDueOrgs.length} Past Due</Tag>}
                  </div>
                  <Table
                    rowKey="id"
                    dataSource={orgs}
                    columns={columns}
                    size="small"
                    pagination={{ pageSize: 20, showTotal: n => `${n} organisations`, style: { padding: '12px 20px', margin: 0 } }}
                    rowClassName={r => r.plan_status === 'past_due' ? 'ant-table-row-warning' : ''}
                    style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
                  scroll={{ x: 'max-content' }}
                  />
                </div>
              ),
            },
            {
              key: 'invoices',
              label: (
                <Space>
                  <FileTextOutlined />
                  Invoices
                  <Tag style={{ marginLeft: 2 }}>{invTotal}</Tag>
                </Space>
              ),
              children: (
                <div>
                  {/* Filing toolbar — date range + CSV export for CA */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', padding: '4px 0 16px' }}>
                    <DatePicker.RangePicker
                      value={dateRange}
                      onChange={setDateRange}
                      allowEmpty={[true, true]}
                      style={{ borderRadius: 8 }}
                    />
                    <Button onClick={reloadInvoices}>Apply filter</Button>
                    <Button
                      type="primary" icon={<FileExcelOutlined />}
                      loading={exporting} onClick={exportCSV}
                    >
                      Export CSV for CA
                    </Button>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Includes taxable value, CGST/SGST/IGST split, GSTIN and export flags.
                    </Text>
                  </div>
                  <div style={{ margin: '0 -20px' }}>
                    <Table
                      rowKey="id"
                      dataSource={invoices}
                      columns={invoiceCols}
                      size="small"
                      pagination={{ pageSize: 20, showTotal: n => `${n} invoices`, style: { padding: '12px 20px', margin: 0 } }}
                      locale={{ emptyText: 'No invoices yet — generated automatically on Pro upgrades and renewals.' }}
                      style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
                      scroll={{ x: 'max-content' }}
                    />
                  </div>
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default AdminBilling;
